/**
 * @voltxampere/schema — B-source expression-driven source params
 * (Sprint 54.2b-3 ADR-0087)
 *
 * SPICE B-element analog. Voltage or current source whose value is
 * evaluated each Newton iteration against an expression DSL. The
 * expression syntax extends Sprint 9 assertion DSL with:
 *
 *   - if/else conditional ternary: `IF (test) ? consequent : alternate`
 *   - pulse() time-domain primitive: `pulse(v1, v2, td, tr, tf, pw, per)`
 *
 * Existing Sprint 9 primitives carry over: V(net), I(component),
 * comparison/arithmetic/math fns.
 *
 * Schema validation here is loose — the expression body is a free
 * `string` (max 1024 chars); the engine's b-source DSL parser
 * surfaces grammar errors at solve time via DCResult.warnings (Sprint
 * 9 assertion runner pattern). Tight grammar parsing at schema time
 * would couple the schema package to the engine's DSL parser —
 * intentionally avoided to keep schema dependency-free.
 *
 * Canonical engine type `b-source`; schema's `ComponentInstance.type`
 * uses the generic string regex (Sprint 7 pattern). This module
 * exports the params schema for future strict-typed callers (Sprint
 * 54.3 fixture builder); ComponentInstance validation still uses
 * the generic `params: z.record(ParamValueSchema)` path.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { z } from "zod";
/**
 * Expression body. Free string up to 1024 chars; DSL grammar
 * enforced at engine evaluate-time. Empty string is rejected (no
 * meaningful B-source can have an empty expression).
 */
const ExpressionStringSchema = z
    .string()
    .min(1, "b-source expression cannot be empty")
    .max(1024, "b-source expression exceeds 1024 character limit");
/**
 * Sprint 54.2b-3 — B-source canonical params schema. Authors place
 * a `b-source` component with `params: { expression, expressionType }`
 * via the standard ComponentInstance schema; this schema is the
 * canonical source-of-truth for the param shape and is consumed by
 * fixture builders + the engine stamp at evaluate time.
 *
 * `expressionType`:
 *   - "voltage": stamp behaves as a voltage source (positive/negative
 *     terminal pair); value = evalDSL(expression) volts.
 *   - "current": stamp behaves as a current source (positive/negative
 *     terminal pair); value = evalDSL(expression) amperes.
 */
export const BSourceParamsSchema = z
    .object({
    expression: ExpressionStringSchema,
    expressionType: z.enum(["voltage", "current"]),
})
    .strict();
//# sourceMappingURL=b-source.js.map